/**
 * Виджет «Шапка сайта» — общая навигация над всеми страницами.
 *
 * Планировщик здесь не отдельное приложение, а один из разделов сайта,
 * поэтому шапка одинаковая и на главной, и в редакторе.
 * Компонент клиентский ради подсветки активного раздела и состояния сессии.
 */
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { routerPaths } from "@/app/routerPaths";
import { EDITOR_OVERLAY_COMPOSITING_STYLE, isDev } from "@/shared/config";
import { useOnlineStatus } from "@/shared/hooks";
import { createProject, useAuthStore, useProjectStore, useThemeStore } from "@/shared/store";
import { UIKit } from "@/shared/ui";
import { FeedbackWidget, useFeedbackStore } from "@/widgets/feedback";
import { buildCatalogShowcaseLayers } from "../logic";
import CloseIcon from "./CloseIcon.svg";
import MenuIcon from "./MenuIcon.svg";
import { ProfileCard } from "./ProfileCard";

const PUBLIC_NAV = [
	{ href: routerPaths.planner, label: "Планировщик" },
	{ href: routerPaths.furnitureGenerator, label: "Конструктор мебели" },
	{ href: routerPaths.blog, label: "Блог" },
];

const PROJECTS_NAV = { href: routerPaths.projects, label: "Мои проекты" };

const isActiveNavItem = (href: string, pathname: string): boolean =>
	href === routerPaths.planner
		? pathname === href || pathname.startsWith("/editor/")
		: pathname === href || pathname.startsWith(`${href}/`);

/**
 * Пункт «Генерация с ИИ» — раздела ещё нет: по клику подсказка, что он в разработке.
 * Пункт виден заранее, чтобы пользователи знали, что такое будет.
 */
const AiGenerationItem = ({ className }: { className: string }) => (
	<UIKit.Tooltip content="В разработке - скоро появится">
		<UIKit.Button variant="header" className={className} disabled>
			Генерация с ИИ
		</UIKit.Button>
	</UIKit.Tooltip>
);

/** Сколько висит результат команды в меню разработки, мс */
const SOON_HINT_MS = 2000;

/** Кнопка смены светлой и тёмной темы интерфейса. */
const ThemeToggle = () => {
	const colorTheme = useThemeStore((state) => state.colorTheme);
	const toggleColorTheme = useThemeStore((state) => state.toggleColorTheme);
	const nextThemeLabel = colorTheme === "light" ? "Включить тёмную тему" : "Включить светлую тему";

	return (
		<UIKit.IconButton
			className="size-8 text-ink hover:bg-accent-soft hover:text-ink"
			icon={colorTheme === "dark" ? <UIKit.IconMoon /> : <UIKit.IconSun />}
			iconSize={17}
			tooltip={{ label: nextThemeLabel }}
			onClick={toggleColorTheme}
		/>
	);
};

/** Локальное меню разработки для переноса текущего проекта в демо-данные. */
const DevMenu = () => {
	const navigate = useNavigate();
	const [isOpen, setIsOpen] = useState(false);
	const [copyStatus, setCopyStatus] = useState<"copied" | "error" | null>(null);

	useEffect(() => {
		if (!copyStatus) return;
		const timer = window.setTimeout(() => setCopyStatus(null), SOON_HINT_MS);
		return () => window.clearTimeout(timer);
	}, [copyStatus]);

	useEffect(() => {
		if (!isOpen) return;
		const closeMenu = () => setIsOpen(false);
		document.addEventListener("click", closeMenu);
		return () => document.removeEventListener("click", closeMenu);
	}, [isOpen]);

	const copyProject = async () => {
		try {
			const { layers } = useProjectStore.getState().project;
			const json = JSON.stringify(layers, null, "\t");
			await navigator.clipboard.writeText(json);
			setCopyStatus("copied");
		} catch {
			setCopyStatus("error");
		}
	};

	return (
		<div className="relative">
			<UIKit.Button
				variant="header"
				className="px-2.5 py-1.5 text-xs font-semibold whitespace-nowrap"
				aria-expanded={isOpen}
				onClick={(event) => {
					event.stopPropagation();
					setIsOpen((open) => !open);
				}}
			>
				DEV Menu
			</UIKit.Button>
			{isOpen && (
				<div className="absolute top-full right-0 z-50 mt-2 w-60 rounded-md border border-border bg-surface p-1 shadow-lg">
					<UIKit.Button
						className="flex w-full justify-start px-3 py-2 text-left text-sm"
						onClick={copyProject}
					>
						{copyStatus === "copied"
							? "JSON скопирован"
							: copyStatus === "error"
								? "Не удалось скопировать"
								: "Скопировать JSON демо"}
					</UIKit.Button>
					<UIKit.Button
						className="flex w-full justify-start px-3 py-2 text-left text-sm"
						onClick={async () => {
							// Сначала уходим с открытого проекта: при выходе он сохраняется, и витрина
							// не должна попасть на сервер под его id
							await navigate(routerPaths.editor3d, { flushSync: true });
							useProjectStore
								.getState()
								.loadProject(
									createProject({ name: "Все модели", layers: buildCatalogShowcaseLayers() }),
								);
						}}
					>
						Проект со всеми моделями
					</UIKit.Button>
				</div>
			)}
		</div>
	);
};

export const SiteHeader = () => {
	const { pathname } = useLocation();
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
	const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
	const isOnline = useOnlineStatus();
	const openFeedback = useFeedbackStore((state) => state.open);
	const navigation = isAuthenticated ? [PROJECTS_NAV, ...PUBLIC_NAV] : PUBLIC_NAV;

	return (
		<div
			className="relative z-80 flex h-12 shrink-0 items-center gap-7 border-b border-border bg-chrome px-4 text-ink"
			style={EDITOR_OVERLAY_COMPOSITING_STYLE}
		>
			<Link to={routerPaths.home} className="flex items-center gap-2 font-bold tracking-tight">
				<img src="/brand/interior-05.png" alt="" className="size-8 shrink-0" />
				{/* Разделитель */}
				<div className="mx-1 h-7 w-px bg-current opacity-25" />
				{/* Название использует общий шрифт приложения. */}
				<div className="flex flex-col leading-none">
					<UIKit.Text as="span" className="text-sm font-bold tracking-widest">
						GRID
					</UIKit.Text>
					<UIKit.Text as="span" className="text-sm font-bold tracking-widest">
						PLANNER
					</UIKit.Text>
				</div>
			</Link>

			<nav className="hidden gap-1 min-[980px]:flex" aria-label="Основная навигация">
				{navigation.map((item) => (
					<UIKit.Button
						key={item.href}
						to={item.href}
						variant="header"
						active={isActiveNavItem(item.href, pathname)}
						aria-current={isActiveNavItem(item.href, pathname) ? "page" : undefined}
						className="px-2.5 py-1.5 text-sm font-semibold"
					>
						{item.label}
					</UIKit.Button>
				))}
				<AiGenerationItem className="px-2.5 py-1.5 text-sm font-semibold" />
				<UIKit.Button
					variant="header"
					className="px-2.5 py-1.5 text-sm font-semibold"
					onClick={openFeedback}
				>
					Оставить отзыв
				</UIKit.Button>
			</nav>

			<div className="ml-auto hidden items-center gap-4 min-[980px]:flex">
				{isDev && <DevMenu />}
				<ThemeToggle />
				{isAuthenticated && (
					<div className="flex items-center gap-2 text-xs text-ink" aria-live="polite">
						<UIKit.Text
							as="span"
							className={`size-2 rounded-full ${isOnline ? "bg-accent" : "bg-danger"}`}
							aria-hidden
						/>
						{isOnline ? "Online" : "Offline"}
					</div>
				)}
				{isAuthenticated ? (
					<ProfileCard />
				) : (
					<Link
						className="rounded-md border border-border px-3.5 py-1.5 text-sm font-bold text-ink hover:border-accent hover:bg-accent-soft hover:text-ink"
						to={routerPaths.login}
					>
						Войти
					</Link>
				)}
			</div>

			<UIKit.Button
				className="ml-auto flex size-9 items-center justify-center border-transparent bg-transparent p-0 text-ink hover:border-transparent hover:bg-accent-soft hover:text-ink min-[980px]:hidden"
				aria-label={isMobileMenuOpen ? "Закрыть меню" : "Открыть меню"}
				aria-expanded={isMobileMenuOpen}
				aria-controls="mobile-navigation"
				onClick={() => setIsMobileMenuOpen((isOpen) => !isOpen)}
			>
				<img
					src={isMobileMenuOpen ? CloseIcon : MenuIcon}
					alt=""
					className="size-6 brightness-0 dark:invert"
				/>
			</UIKit.Button>

			{isMobileMenuOpen && (
				<nav
					id="mobile-navigation"
					className="absolute inset-x-0 top-full z-50 border-t border-border bg-chrome px-4 py-3 shadow-lg min-[980px]:hidden"
					aria-label="Мобильная навигация"
				>
					<div className="flex flex-col gap-1">
						{navigation.map((item) => (
							<UIKit.Button
								key={item.href}
								to={item.href}
								variant="header"
								active={isActiveNavItem(item.href, pathname)}
								aria-current={isActiveNavItem(item.href, pathname) ? "page" : undefined}
								onClick={() => setIsMobileMenuOpen(false)}
								className="flex w-full justify-start px-3 py-2 text-left text-sm font-bold"
							>
								{item.label}
							</UIKit.Button>
						))}
						<AiGenerationItem className="flex w-full justify-start px-3 py-2 text-left text-sm font-bold" />
						<UIKit.Button
							variant="header"
							className="flex w-full justify-start px-3 py-2 text-left text-sm font-bold"
							onClick={() => {
								setIsMobileMenuOpen(false);
								openFeedback();
							}}
						>
							Оставить отзыв
						</UIKit.Button>
					</div>
					<div className="mt-3 border-t border-border pt-3">
						<ThemeToggle />
					</div>
					<div className="mt-3 border-t border-border pt-3">
						{isAuthenticated ? (
							<ProfileCard />
						) : (
							<Link
								className="block rounded-md border border-border px-3 py-2 text-center text-sm font-bold text-ink hover:border-accent hover:bg-accent-soft hover:text-ink"
								to={routerPaths.login}
								onClick={() => setIsMobileMenuOpen(false)}
							>
								Войти
							</Link>
						)}
					</div>
				</nav>
			)}
			<FeedbackWidget />
		</div>
	);
};
